import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import {
    TimerIcon,
    CheckCircleIcon,
    AlertTriangleIcon,
    RefreshCwIcon,
    ArrowUpIcon,
    ArrowDownIcon,
} from "lucide-react";
import { ApplicantInsight } from "@type/applicant";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { getApplicantInsight } from "@service/job";
import { formatHumanReadable } from "@util/date";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";

interface Props {
    job_id: string
}

export function ApplicantInsights({ job_id }: Props) {
    const [insights, setInsights] = useState<ApplicantInsight>({
        prev_total_assessment_completion_rate: 0,
        total_assessment_completion_rate: 0,
        total_candidate_invited: 0,
        total_candidate_completed: 0,
        total_candidate_pending: 0,

        prev_total_average_assessment_duration_minutes: 0,
        total_interview_drop_off_rate: 0,
        total_candidate_dropped_off: 0,
        total_candidate_partial_interview: 0,
        total_candidate_aborted_interview: 0,

        prev_total_interview_drop_off_rate: 0,
        total_average_assessment_duration_minutes: 0,
        total_minimum_assessment_duration_minutes: 0,
        total_maximum_assessment_duration_minutes: 0,

        top_skills: [],
        top_locations: [],
        top_titles: [],
        top_matche_scores: [],
    });
    const [isLoadingInsight, setLoadingApplicantInsight] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [timeRange, setTimeRange] = useState<"7" | "30" | "90" | "all">("all");

    const fetchInsights = async (job_id: string, days: string) => {
        setLoadingApplicantInsight(true);
        getApplicantInsight(job_id, days).then((resp) => {
            if (resp.status === 200) {
                setInsights(resp.data.data);
            }
        }).finally(() => {
            setLoadingApplicantInsight(false);
            setIsRefreshing(false);
        });
    }

    useEffect(() => {
        fetchInsights(job_id, timeRange);
    }, [job_id, timeRange]);

    const formatDuration = (minutes: number): { value: string; unit: string } => {
        if (minutes > 1440) {
            return { value: (minutes / 1440).toFixed(1), unit: "days" };
        }
        if (minutes > 60) {
            return { value: (minutes / 60).toFixed(1), unit: "hrs" };
        }
        return { value: minutes.toFixed(0), unit: "min" };
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchInsights(job_id, timeRange)
    }

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    Applicant Insights
                </h2>
                <div className="flex items-center gap-3">
                    {/* Showing metrics for dropdown */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">Showing metrics for:</span>
                        <Select
                            value={timeRange}
                            onValueChange={(value) => setTimeRange(value as "7" | "30" | "90" | "all")}
                        >
                            <SelectTrigger className="h-8 w-[140px] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                                <SelectItem value="all">All-time</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Last updated + Refresh */}
            <div className="flex items-center gap-2 mb-4">
                <p className="text-sm text-muted-foreground">
                    Last updated: {insights.last_updated_at ? formatHumanReadable(insights.last_updated_at, true) : "N/A"}
                </p>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 hover:bg-transparent"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                >
                    <RefreshCwIcon
                        className={`h-3 w-3 text-muted-foreground hover:text-foreground transition-colors ${isRefreshing ? "animate-spin" : ""
                            }`}
                    />
                </Button>
            </div>
            <div className="space-y-6 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Assessment Completion Rate */}
                    <div
                        className={cn("bg-card border border-border rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all duration-200 relative min-w-[280px]", isLoadingInsight && "animate-pulse")}
                        style={{
                            paddingTop: 24,
                            paddingRight: 24,
                            paddingBottom: 24,
                            paddingLeft: 24,
                        }}
                    >
                        <div className="flex items-center gap-4 mb-3">
                            {/* Icon */}
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 flex items-center justify-center bg-blue-50 dark:bg-blue-950 rounded-lg">
                                    <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                                        AI Screening Completion Rate
                                    </p>
                                    {/* Trend Indicator */}
                                    {insights.prev_total_assessment_completion_rate !== 0 && (
                                        <div className={cn("flex items-center gap-1 text-sm font-medium", {
                                            "text-green-600": insights.prev_total_assessment_completion_rate > 0,
                                            "text-red-600": insights.prev_total_assessment_completion_rate < 0,
                                        })}>
                                            {insights.prev_total_assessment_completion_rate < 0 ? (
                                                <>
                                                    <ArrowDownIcon className="w-3 h-3" />
                                                    <span>{Math.abs(insights.prev_total_assessment_completion_rate).toFixed(1)}%</span>
                                                </>
                                            ) : (
                                                <>
                                                    <ArrowUpIcon className="w-3 h-3" />
                                                    <span>{(insights.prev_total_assessment_completion_rate).toFixed(1)}%</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <p className="text-3xl font-bold text-foreground mr-1">
                                        {insights.total_assessment_completion_rate.toFixed(0)}%
                                    </p>
                                    <span className="text-gray-500">({insights.total_candidate_invited} invited)</span>
                                </div>
                            </div>
                        </div>
                        {/* Breakdown */}
                        <div className="text-sm">
                            {insights.total_assessment_completion_rate > 0 ? (
                                <ul className="space-y-1 text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] m-0 p-0">
                                    <li className="flex items-start m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                        <span><span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{insights.total_candidate_completed}</span> completed</span>
                                    </li>
                                    <li className="flex items-start m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                        <span><span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{insights.total_candidate_pending}</span> pending</span>
                                    </li>
                                </ul>
                            ) : (
                                <span className="text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] font-normal">
                                        No screenings completed yet.
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Drop-off Rates */}
                    <div
                        className={cn("bg-card border border-border rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all duration-200 relative min-w-[280px]", isLoadingInsight && "animate-pulse")}
                        style={{
                            paddingTop: 24,
                            paddingRight: 24,
                            paddingBottom: 24,
                            paddingLeft: 24,
                        }}
                    >
                        <div className="flex items-center gap-4 mb-3">
                            {/* Icon */}
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 flex items-center justify-center bg-blue-50 dark:bg-blue-950 rounded-lg">
                                    <AlertTriangleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                                        AI Interview Drop-off Rate
                                    </p>
                                    {/* Trend Indicator */}
                                    {insights.prev_total_interview_drop_off_rate !== 0 && (
                                        <div className={cn("flex items-center gap-1 text-sm font-medium", {
                                            "text-red-600": insights.prev_total_interview_drop_off_rate > 0,
                                            "text-green-600": insights.prev_total_interview_drop_off_rate < 0,
                                        })}>
                                            {insights.prev_total_interview_drop_off_rate < 0 ? (
                                                <>
                                                    <ArrowDownIcon className="w-3 h-3" />
                                                    <span>{Math.abs(insights.prev_total_interview_drop_off_rate).toFixed(1)}%</span>
                                                </>
                                            ) : (
                                                <>
                                                    <ArrowUpIcon className="w-3 h-3" />
                                                    <span>{(insights.prev_total_interview_drop_off_rate).toFixed(1)}%</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <p className="text-3xl font-bold text-foreground mr-1">
                                        {insights.total_interview_drop_off_rate.toFixed(0)}%
                                    </p>
                                    <span className="text-gray-500">({insights.total_candidate_dropped_off} dropped off)</span>
                                </div>
                            </div>
                        </div>
                        {/* Breakdown */}
                        <div className="text-sm">
                            {insights.total_interview_drop_off_rate > 0 ? (
                                <ul className="space-y-1 text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] m-0 p-0">
                                    <li className="flex items-start mb-1 m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                        <span><span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{insights.total_candidate_partial_interview}</span> partial {insights.total_candidate_partial_interview > 1 ? 'interviews' : 'interview'}</span>
                                    </li>
                                    <li className="flex items-start m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                        <span><span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{insights.total_candidate_aborted_interview}</span> aborted {insights.total_candidate_aborted_interview > 1 ? 'interviews' : 'interview'}</span>
                                    </li>
                                </ul>
                            ) : (
                                <span className="text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] font-normal">
                                    No drop-offs recorded.
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Average Assessment Duration */}
                    <Tooltip content="Median time taken to complete screening.">
                        <div
                            className={cn("bg-card border border-border rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all duration-200 relative min-w-[280px] min-h-[166px]", isLoadingInsight && "animate-pulse")}
                            style={{
                                paddingTop: 24,
                                paddingRight: 24,
                                paddingBottom: 24,
                                paddingLeft: 24,
                            }}
                        >
                            <div className="flex items-center gap-4 mb-3">
                                {/* Icon */}
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 flex items-center justify-center bg-blue-50 dark:bg-blue-950 rounded-lg">
                                        <TimerIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                                            Median AI Screening Duration
                                        </p>
                                        {/* Trend Indicator */}
                                        {/* {insights.prev_total_average_assessment_duration_minutes !== 0 && (
                                            <div className={cn("flex items-center gap-1 text-sm font-medium", {
                                                "text-green-600": insights.prev_total_average_assessment_duration_minutes > 0,
                                                "text-red-600": insights.prev_total_average_assessment_duration_minutes < 0,
                                            })}>
                                                {insights.prev_total_average_assessment_duration_minutes < 0 ? (
                                                    <>
                                                        <ArrowDownIcon className="w-3 h-3" />
                                                        <span>{(Math.abs(insights.prev_total_average_assessment_duration_minutes).toFixed(1))}%</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ArrowUpIcon className="w-3 h-3" />
                                                        <span>{(insights.prev_total_average_assessment_duration_minutes).toFixed(1)}%</span>
                                                    </>
                                                )}
                                            </div>
                                        )} */}
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <p className="text-3xl font-bold text-foreground">
                                            {formatDuration(insights.total_average_assessment_duration_minutes).value}
                                            <span className="text-sm font-normal text-muted-foreground ml-1">
                                                {formatDuration(insights.total_average_assessment_duration_minutes).unit}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Breakdown */}
                            <div className="text-sm">
                                {insights.total_average_assessment_duration_minutes > 0 ? (
                                    <div>
                                        <ul className="space-y-1 text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] m-0 p-0">
                                            <li className="flex items-start m-0 p-0">
                                                <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                                <span>Minimum duration: <span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{formatDuration(insights.total_minimum_assessment_duration_minutes).value} {formatDuration(insights.total_minimum_assessment_duration_minutes).unit}</span></span>
                                            </li>
                                            <li className="flex items-start m-0 p-0">
                                                <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0 mr-2" />
                                                <span>Maximum duration: <span className="font-medium text-[hsl(240_10%_3.9%)] dark:text-[hsl(0_0%_98%)]">{formatDuration(insights.total_maximum_assessment_duration_minutes).value} {formatDuration(insights.total_maximum_assessment_duration_minutes).unit}</span></span>
                                            </li>
                                        </ul>
                                    </div>
                                ) : (
                                    <span className="text-[hsl(240_3.8%_46.1%)] dark:text-[hsl(240_5%_64.9%)] font-normal">
                                        No screening data available.
                                    </span>
                                )}
                            </div>
                        </div>
                    </Tooltip>
                </div>

                {/* Simplified Insights Row */}
                <Card className={cn("border border-border", isLoadingInsight && "animate-pulse")}>
                    <CardHeader>
                        <CardTitle className="text-base font-semibold">
                            Top Insights
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                            Most common attributes across applicants for this job listing.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {/* Top Skills - Bar Graph */}
                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    Skills
                                </h4>
                                <div className="space-y-2">
                                    {(insights.top_skills || []).length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <p className="text-sm text-muted-foreground">
                                                No skill data available.
                                            </p>
                                        </div>
                                    ) : (
                                        insights.top_skills.map((item, index) => {
                                            const maxCount = Math.max(
                                                ...insights.top_skills.map((s) => s.count)
                                            );
                                            const widthPercentage = (item.count / maxCount) * 100;
                                            return (
                                                <div key={index} className="relative">
                                                    <div
                                                        className="h-8 rounded bg-blue-100 dark:bg-blue-950/30 flex items-center px-3 transition-all"
                                                        style={{
                                                            width: `${Math.max(widthPercentage, 25)}%`,
                                                        }}
                                                    >
                                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                                                            {item.skill}
                                                        </span>
                                                    </div>
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                                                        {item.count}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            {/* Top Locations - Bar Graph */}
                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    Location
                                </h4>
                                <div className="space-y-2">
                                    {(insights.top_locations || []).length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <p className="text-sm text-muted-foreground">
                                                No location data available.
                                            </p>
                                        </div>
                                    ) : (
                                        insights.top_locations.map((item, index) => {
                                            const maxCount = Math.max(
                                                ...insights.top_locations.map((l) => l.count)
                                            );
                                            const widthPercentage = (item.count / maxCount) * 100;
                                            return (
                                                <div key={index} className="relative">
                                                    <div
                                                        className="h-8 rounded bg-blue-100 dark:bg-blue-950/30 flex items-center px-3 transition-all"
                                                        style={{
                                                            width: `${Math.max(widthPercentage, 25)}%`,
                                                        }}
                                                    >
                                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                                                            {item.location}
                                                        </span>
                                                    </div>
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                                                        {item.count}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            {/* Top Job Titles - Bar Graph */}
                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    Job Titles
                                </h4>
                                <div className="space-y-2">
                                    {(insights.top_titles || []).length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <p className="text-sm text-muted-foreground">
                                                No job title data available.
                                            </p>
                                        </div>
                                    ) : (
                                        insights.top_titles.map((item, index) => {
                                            const maxCount = Math.max(
                                                ...insights.top_titles.map((t) => t.count)
                                            );
                                            const widthPercentage = (item.count / maxCount) * 100;
                                            return (
                                                <div key={index} className="relative">
                                                    <div
                                                        className="h-8 rounded bg-blue-100 dark:bg-blue-950/30 flex items-center px-3 transition-all"
                                                        style={{
                                                            width: `${Math.max(widthPercentage, 25)}%`,
                                                        }}
                                                    >
                                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                                                            {item.title}
                                                        </span>
                                                    </div>
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                                                        {item.count}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    Match Score Levels
                                </h4>
                                <div className="space-y-2">
                                    {(insights.top_matche_scores || []).length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <p className="text-sm text-muted-foreground">
                                                No match score data available.
                                            </p>
                                        </div>
                                    ) : (
                                        (insights.top_matche_scores || []).map((item, index) => {
                                            const maxCount = Math.max(
                                                ...(insights.top_matche_scores || []).map((l) => l.count)
                                            );
                                            const widthPercentage = (item.count / maxCount) * 100;

                                            // Determine color based on match level
                                            let bgColor = "bg-blue-100 dark:bg-blue-950/30";
                                            let textColor = "text-blue-900 dark:text-blue-100";

                                            if (item.level === "high") {
                                                bgColor = "bg-green-100 dark:bg-green-900/30";
                                                textColor = "text-green-900 dark:text-green-100";
                                            } else if (item.level === "medium") {
                                                bgColor = "bg-yellow-100 dark:bg-yellow-900/30";
                                                textColor = "text-yellow-900 dark:text-yellow-100";
                                            } else if (item.level === "low") {
                                                bgColor = "bg-red-100 dark:bg-red-900/30";
                                                textColor = "text-red-900 dark:text-red-100";
                                            }

                                            return (
                                                <div key={index} className="relative">
                                                    <div
                                                        className={`h-8 rounded ${bgColor} flex items-center px-3 transition-all`}
                                                        style={{
                                                            width: `${Math.max(widthPercentage, 25)}%`,
                                                        }}
                                                    >
                                                        <span
                                                            className={`text-sm font-medium ${textColor} truncate`}
                                                        >
                                                            {item.score_range}
                                                        </span>
                                                    </div>
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                                                        {item.count}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}